import { Circle, Group, Path } from 'fabric';
import { GARDEN_OBJECT_TYPES } from '../../../constants/gardenObjectTypes';
import { FLOWER_COLOR_MAP, hexToRgba } from '../../../constants/flowerColors';
import { getIconPathForGrowthType } from './plantIconPaths';
export { FLOWER_COLOR_MAP } from '../../../constants/flowerColors';

/**
 * PlantCircle - Custom Fabric.js Circle for plant placement
 *
 * Features:
 * - Renders plant with two-ring spacing visualization
 * - Inner ring = min_width_cm / 2 (physical minimum footprint)
 * - Outer ring = max_width_cm / 2 (optimal growth extent, dashed, hidden by default)
 * - Displays plant icon based on care_growth_type (Lucide icons) centered
 * - Shows plant name label below circle
 * - Locked controls (move only, no resize/rotate)
 * - Three-state overlap detection: none / warning / critical
 * - Stores all plant properties as metadata
 *
 * Circle sizing:
 * - innerRadius = min_width_cm / 2 (physical minimum)
 * - outerRadius = max_width_cm / 2 (optimal extent)
 * - Fallback: both = space_requirement_cm MAX / 2 (legacy behavior)
 *
 * Overlap states & colors:
 * - none:     Emerald stroke, flower-color or emerald fill
 * - warning:  Emerald inner stroke, amber outer stroke/fill
 * - critical: Red stroke on both rings
 */

/**
 * Parse space_requirement_cm string to get maximum spacing value
 *
 * @param {string|null} spaceRequirement - Format: "MIN-MAX" (e.g., "30-50") or null
 * @returns {number} Maximum spacing in cm, or default 60cm if null/invalid
 *
 * @example
 * parseSpacingValue("30-50") // returns 50
 * parseSpacingValue("25")    // returns 25
 * parseSpacingValue(null)    // returns 60 (default)
 */
export function parseSpacingValue(spaceRequirement) {
  if (!spaceRequirement) {
    return 60; // Default spacing: 60cm (radius 30cm)
  }

  // Handle range format "MIN-MAX"
  if (typeof spaceRequirement === 'string' && spaceRequirement.includes('-')) {
    const parts = spaceRequirement.split('-');
    const maxValue = parseInt(parts[1], 10);
    return isNaN(maxValue) ? 60 : maxValue;
  }

  // Handle single value
  const value = parseInt(spaceRequirement, 10);
  return isNaN(value) ? 60 : value;
}

/**
 * Get plant display name (prefers name over german_name)
 *
 * @param {object} plant - Plant object
 * @returns {string} Display name
 */
function getPlantDisplayName(plant) {
  return plant.name || plant.german_name || 'Unbekannte Pflanze';
}


/**
 * PlantCircle class - extends Fabric.js Group
 * Groups together: outer circle, inner circle, and icon
 */
export class PlantCircle extends Group {
  /**
   * Create a new PlantCircle
   *
   * @param {object} plant - Full plant object from API
   * @param {number} plant.id - Plant ID
   * @param {string} plant.name - Plant name (german_name)
   * @param {string} plant.scientific_name - Scientific name
   * @param {object} plant.properties - Plant properties object
   * @param {string} plant.properties.space_requirement_cm - Spacing requirement "MIN-MAX"
   * @param {number} plant.properties.min_width_cm - Minimum growth width in cm
   * @param {number} plant.properties.max_width_cm - Maximum growth width in cm
   * @param {string} plant.properties.growth_type - Growth type (shrub, tree, perennial, etc.)
   * @param {object} options - Fabric.js options (left, top, etc.)
   */
  constructor(plant, options = {}) {
    // Extract plant properties (support both flat and nested formats)
    const properties = plant.properties || {};
    const spaceRequirementCm = properties.space_requirement_cm || null;
    const spacingCm = parseSpacingValue(spaceRequirementCm);

    // Two-radius calculation
    const minWidthCm = plant.min_width_cm ?? properties.min_width_cm ?? null;
    const maxWidthCm = plant.max_width_cm ?? properties.max_width_cm ?? null;

    // Per-instance size override (#754): a value inside the DB span concretises
    // "which exemplar on the known spectrum is mine" (e.g. a pruned hedge or a
    // smaller-growing variety). null = use the DB default. The width override
    // drives the footprint (outer) ring; height only affects 3D (no 2D height).
    const customWidthCm = options.customWidthCm ?? null;
    const { innerRadius, outerRadius } = PlantCircle._resolveRadii({
      minWidthCm,
      maxWidthCm,
      spacingCm,
      customWidthCm,
    });

    const displayName = getPlantDisplayName(plant);

    // Default colors (normal state - emerald)
    const normalFill = 'rgba(16, 185, 129, 0.2)'; // Emerald-500 at 20% opacity
    const normalStroke = '#10B981'; // Emerald-500
    const iconColor = '#059669'; // Emerald-600

    // Create outer circle (dashed, hidden by default — shown on hover/selection)
    const outerCircle = new Circle({
      radius: outerRadius,
      fill: 'transparent',
      stroke: '#34D399', // Emerald-400
      strokeWidth: 1.5,
      strokeDashArray: [8, 4],
      originX: 'center',
      originY: 'center',
      visible: false,
    });

    // Create inner circle (main visible ring)
    const innerCircle = new Circle({
      radius: innerRadius,
      fill: normalFill,
      stroke: normalStroke,
      strokeWidth: 2,
      originX: 'center',
      originY: 'center',
    });

    // Create plant icon based on growth_type
    const growthType = properties.growth_type || null;
    const iconPath = new Path(getIconPathForGrowthType(growthType), {
      fill: 'transparent',
      stroke: iconColor,
      strokeWidth: 2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      scaleX: 1,
      scaleY: 1,
      originX: 'center',
      originY: 'center',
      left: 0,
      top: 0,
    });

    // Group: outerCircle (back) → innerCircle → icon (front)
    const objects = [outerCircle, innerCircle, iconPath];
    super(objects, {
      left: options.left || 0,
      top: options.top || 0,
      originX: 'center',
      originY: 'center',
      selectable: true,
      perPixelTargetFind: false, // Bounding-box hit detection (circles have clean AABB)
      hasControls: false, // No resize/rotate controls
      hasBorders: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      lockSkewingX: true,
      lockSkewingY: true,
      noScaleCache: true, // Plants are never scaled; skip cache regeneration on viewport zoom
      ...options,
    });

    // Initialize `data` eagerly so consumers that read `obj.data` directly after
    // `canvas.add()` don't see `undefined`. GardenCanvas.assignLayerToObject only
    // assigns `data` inside the `object:added` handler; if the TabBar counter's
    // `object:added` handler (filter starts with `obj.data && …`) runs first, the
    // plant would be excluded from the count until the next add/remove event.
    // The drag-drop path has no ghost-cleanup `object:removed` to trigger a recount
    // (unlike click-to-place), so without this it stays "0 Objekte". `assignLayerToObject`
    // merges `layer`/`zIndexWithinLayer` additively onto this object. `type: 'plant'`
    // is not a WIZARD_TYPE_TO_LAYER key, so layer routing still uses objectType.
    this.data = { type: 'plant' };

    // Store plant metadata
    this.objectType = GARDEN_OBJECT_TYPES.PLANT;
    this.plantId = plant.id;
    this.plantName = displayName;
    this.scientificName = plant.scientific_name || '';
    this.spacingCm = spacingCm;
    this.plantProperties = { ...plant }; // Store full plant object

    // DB width span + per-instance size override (#754). The span bounds the
    // slider in the properties panel; the override values (null = DB default)
    // feed both the 2D footprint ring and the 3D mesh (via getPlantSizeAt).
    this._minWidthCm = minWidthCm;
    this._maxWidthCm = maxWidthCm;
    this.customHeightCm = options.customHeightCm ?? null;
    this.customWidthCm = customWidthCm;

    // Three-state overlap: 'none' | 'warning' | 'critical'
    this.overlapState = 'none';
    // Backward-compat getter (used by persistence cleanup and context menu)
    this.isOverlapping = false;

    // Selected flower color (DB key like 'red', 'blue', etc. — null = emerald default)
    this.selectedColor = options.selectedColor || null;

    // Store references to child objects for easy access
    this._outerCircle = outerCircle;
    this._innerCircle = innerCircle;
    this._icon = iconPath;

    // Apply initial state
    this._updateColors();
  }

  /**
   * Resolve inner/outer ring radii (px) from the DB width span, spacing
   * fallback, and an optional per-instance width override (#754).
   *
   * Inner ring = physical minimum footprint (min_width/2, unaffected by the
   * override). Outer ring = growth extent: the override wins over max_width;
   * without a usable span both rings fall back to a single value.
   *
   * @param {{minWidthCm: ?number, maxWidthCm: ?number, spacingCm: number, customWidthCm: ?number}} p
   * @returns {{innerRadius: number, outerRadius: number}}
   * @private
   */
  static _resolveRadii({ minWidthCm, maxWidthCm, spacingCm, customWidthCm }) {
    const effectiveMaxCm =
      customWidthCm != null && customWidthCm > 0 ? customWidthCm : maxWidthCm;

    if (minWidthCm != null && effectiveMaxCm != null && minWidthCm > 0 && effectiveMaxCm > 0) {
      // Guard against an override below the physical minimum (span-bounded in
      // the UI, but keep the invariant outer >= inner if called directly).
      return {
        innerRadius: minWidthCm / 2,
        outerRadius: Math.max(effectiveMaxCm, minWidthCm) / 2,
      };
    }

    // Fallback: single-ring behaviour from the override or the spacing default.
    const single = customWidthCm != null && customWidthCm > 0 ? customWidthCm : spacingCm;
    return { innerRadius: single / 2, outerRadius: single / 2 };
  }

  /**
   * Re-resolve and apply the ring radii after a width override change (#754).
   * @private
   */
  _applyWidthOverride() {
    if (!this._innerCircle || !this._outerCircle) return; // guard during construction
    const { innerRadius, outerRadius } = PlantCircle._resolveRadii({
      minWidthCm: this._minWidthCm,
      maxWidthCm: this._maxWidthCm,
      spacingCm: this.spacingCm,
      customWidthCm: this.customWidthCm,
    });
    this._innerCircle.set({ radius: innerRadius });
    this._outerCircle.set({ radius: outerRadius });
    this.dirty = true;
    // Refresh the interactive bbox after the child geometry changed (real Fabric
    // Group only; the getRadius() overlap path reads the child radius directly).
    if (typeof this.setCoords === 'function') {
      this.setCoords();
    }
  }

  /**
   * Update circle/icon colors based on overlapState.
   *
   * Separation of concerns (ADR-2):
   *   Inner Fill   → Blütenfarbe (selectedColor) or Emerald default
   *   Inner Stroke → Status: Emerald (none), Emerald (warning), Red (critical)
   *   Outer Stroke → Status: Emerald-400 (none), Amber-500 (warning), Red-500 (critical)
   *   Outer Fill   → transparent (none), Amber 10% (warning), Red 10% (critical)
   *   Icon         → Darker variant or status color
   * @private
   */
  _updateColors() {
    if (!this._innerCircle || !this._icon) return; // guard during construction

    // --- Inner Fill: Blütenfarbe or Emerald default (unchanged by overlap state) ---
    if (this.selectedColor && FLOWER_COLOR_MAP[this.selectedColor]) {
      const { hex } = FLOWER_COLOR_MAP[this.selectedColor];
      this._innerCircle.set({ fill: hexToRgba(hex, 0.25) });
    } else {
      this._innerCircle.set({ fill: 'rgba(16, 185, 129, 0.2)' });
    }

    // --- Three-state color logic ---
    switch (this.overlapState) {
      case 'critical':
        this._innerCircle.set({ stroke: '#EF4444' }); // Red-500
        this._icon.set({ stroke: '#DC2626' });         // Red-600
        if (this._outerCircle) {
          this._outerCircle.set({
            stroke: '#EF4444',                          // Red-500
            fill: 'rgba(239, 68, 68, 0.1)',             // Red 10%
            strokeDashArray: [8, 4],
          });
        }
        break;

      case 'warning':
        this._innerCircle.set({ stroke: '#10B981' }); // Emerald-500 (inner stays green)
        this._icon.set({ stroke: '#D97706' });         // Amber-600
        if (this._outerCircle) {
          this._outerCircle.set({
            stroke: '#F59E0B',                          // Amber-500
            fill: 'rgba(245, 158, 11, 0.1)',            // Amber 10%
            strokeDashArray: [8, 4],
          });
        }
        break;

      default: // 'none'
        this._innerCircle.set({ stroke: '#10B981' }); // Emerald-500
        this._icon.set({ stroke: this._getDarkerIconColor(this.selectedColor) });
        if (this._outerCircle) {
          this._outerCircle.set({
            stroke: '#34D399',                          // Emerald-400
            fill: 'transparent',
            strokeDashArray: [8, 4],
          });
        }
        break;
    }
  }

  /**
   * Returns a darker shade for use as icon stroke color.
   * Falls back to Emerald-600 for unknown/null keys.
   * @private
   */
  _getDarkerIconColor(colorKey) {
    const darkerMap = {
      white:  '#9CA3AF', // Gray-400 (white is not darkened)
      yellow: '#CA8A04', // Yellow-600
      orange: '#EA580C', // Orange-600
      red:    '#DC2626', // Red-600
      pink:   '#DB2777', // Pink-600
      purple: '#9333EA', // Purple-600
      violet: '#7C3AED', // Violet-600
      blue:   '#2563EB', // Blue-600
      green:  '#059669', // Emerald-600
    };
    return darkerMap[colorKey] || '#059669';
  }

  /**
   * Override set() to re-apply colors when selectedColor changes.
   */
  set(key, value) {
    const result = super.set(key, value);
    const changed = (prop) =>
      typeof key === 'string'
        ? key === prop
        : typeof key === 'object' && key !== null && prop in key;
    if (changed('selectedColor')) {
      this._updateColors();
    }
    // Width override (#754) resizes the footprint ring so 2D and 3D agree.
    if (changed('customWidthCm')) {
      this._applyWidthOverride();
    }
    return result;
  }

  /**
   * Set three-state overlap and update colors.
   *
   * @param {'none'|'warning'|'critical'} state - Overlap state
   */
  setOverlapState(state) {
    if (this.overlapState !== state) {
      this.overlapState = state;
      // Backward-compat flag for persistence cleanup
      this.isOverlapping = state !== 'none';
      this._updateColors();

      // Auto-show outer ring when overlapping so the user sees the conflict zone;
      // hide it again when overlap clears (unless held open by hover/selection).
      if (this._outerCircle) {
        if (state !== 'none') {
          this._overlapShowsRing = true;
        } else {
          this._overlapShowsRing = false;
        }
        this._applyOuterRingVisibility();
      }

      this.canvas?.requestRenderAll();
    }
  }

  /**
   * Legacy setter — maps boolean to three-state.
   * Used by overlapDetection.js until Chunk C updates it.
   *
   * @param {boolean} overlapping
   */
  setOverlapping(overlapping) {
    this.setOverlapState(overlapping ? 'critical' : 'none');
  }

  /**
   * Get inner radius (physical minimum footprint) in pixels, scaled.
   * @returns {number}
   */
  getInnerRadius() {
    return this._innerCircle.radius * this.scaleX;
  }

  /**
   * Get outer radius (optimal growth extent) in pixels, scaled.
   * @returns {number}
   */
  getOuterRadius() {
    return this._outerCircle.radius * this.scaleX;
  }

  /**
   * Get circle radius for overlap detection.
   * Returns outer radius for backward compatibility with overlapDetection.js.
   *
   * @returns {number} Outer circle radius in pixels (scaled)
   */
  getRadius() {
    return this.getOuterRadius();
  }

  /** Show the outer (dashed) ring on hover */
  showOuterRing() {
    if (this._outerCircle) {
      this._hoverShowsRing = true;
      this._applyOuterRingVisibility();
    }
  }

  /** Hide the outer (dashed) ring on mouse-out */
  hideOuterRing() {
    if (this._outerCircle) {
      this._hoverShowsRing = false;
      this._applyOuterRingVisibility();
    }
  }

  /** Show the outer (dashed) ring on selection */
  selectOuterRing() {
    if (this._outerCircle) {
      this._selectionShowsRing = true;
      this._applyOuterRingVisibility();
    }
  }

  /** Hide the outer (dashed) ring on deselection */
  deselectOuterRing() {
    if (this._outerCircle) {
      this._selectionShowsRing = false;
      this._applyOuterRingVisibility();
    }
  }

  /**
   * Update outer ring visibility based on all flags.
   * Ring is visible if ANY reason holds it open (hover, selection, overlap).
   * @private
   */
  _applyOuterRingVisibility() {
    this._outerCircle.visible = !!(this._hoverShowsRing || this._selectionShowsRing || this._overlapShowsRing);
    this.dirty = true;
  }

  /**
   * Get circle center position (absolute canvas coordinates)
   *
   * @returns {object} {x, y} center position
   */
  getCenterPosition() {
    return {
      x: this.left,
      y: this.top,
    };
  }

  /**
   * Convert PlantCircle to JSON for persistence
   * Overrides Fabric.js toObject() to include custom properties
   *
   * @returns {object} Serialized object
   */
  toObject() {
    return {
      ...super.toObject(),
      // Nicht-destruktiver Blüh-Modus (#608): ist eine Blüh-Darstellung aktiv,
      // speichere die Original-Opacity, nicht die transient gedimmte — Speichern
      // im Blüh-Modus darf den Plan nicht verändern.
      opacity: this._bloomStash ? this._bloomStash.opacity : this.opacity,
      objectType: this.objectType,
      plantId: this.plantId,
      plantName: this.plantName,
      scientificName: this.scientificName,
      spacingCm: this.spacingCm,
      plantProperties: this.plantProperties,
      overlapState: this.overlapState,
      isOverlapping: this.isOverlapping, // backward-compat for persistence cleanup
      selectedColor: this.selectedColor,
      // Per-instance size override (#754); null = DB default. Read by
      // buildPlantSceneEntry (3D) and restored in fromObject (2D ring).
      customHeightCm: this.customHeightCm,
      customWidthCm: this.customWidthCm,
      // Custom per-object metadata (layer, per-object hide/lock #760, objectId).
      // Unlike toJSON(['data']) objects, PlantCircle serializes via toObject(),
      // which drops unknown props — so data must be included explicitly or a
      // hidden/locked plant would reappear on reload.
      data: this.data,
    };
  }

  /**
   * Create PlantCircle from JSON (for deserialization).
   * Migrates old data: if overlapState is missing, derives it from isOverlapping boolean.
   * Always rebuilds from plantProperties so new radius logic is applied.
   *
   * @param {object} object - Serialized object from toObject()
   * @param {function} callback - Callback function(plantCircle)
   * @returns {PlantCircle} Restored PlantCircle instance
   */
  static fromObject(object, callback) {
    const plant = object.plantProperties;
    const options = {
      left: object.left,
      top: object.top,
      scaleX: object.scaleX,
      scaleY: object.scaleY,
      angle: object.angle,
    };

    const plantCircle = new PlantCircle(plant, {
      ...options,
      selectedColor: object.selectedColor || null,
      // Restore per-instance size override (#754); older saves have neither → DB default.
      customHeightCm: object.customHeightCm ?? null,
      customWidthCm: object.customWidthCm ?? null,
    });

    // Restore custom metadata (layer, per-object hide/lock #760, objectId),
    // keeping the constructor's type marker. Older saves have no data → no-op.
    if (object.data) {
      plantCircle.data = { ...plantCircle.data, ...object.data };
    }

    // Migrate: old boolean → three-state
    if (object.overlapState) {
      plantCircle.setOverlapState(object.overlapState);
    } else if (object.isOverlapping) {
      plantCircle.setOverlapState('critical');
    }
    // else: default 'none' from constructor is correct

    if (callback) {
      callback(plantCircle);
    }

    return plantCircle;
  }
}

// NOTE: Fabric.js v6 doesn't support automatic deserialization via fabric.PlantCircle
// PlantCircle.fromObject() must be called manually when loading from JSON
// See GardenCanvas loadCanvas() for custom class restoration logic
